import { useState } from "react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "./ui/accordion";
import { Card } from "./ui/card";
import { Badge } from "./ui/badge";
import { Button } from "./ui/button";
import { HelpCircle, Search, ChevronDown } from "lucide-react";

interface FAQItem {
  question: string;
  answer: string;
}

interface FAQProps {
  title?: string;
  subtitle?: string;
  items: FAQItem[];
  accentColor?: 'purple' | 'teal' | 'blue' | 'orange' | 'indigo';
  maxItems?: number;
}

export function FAQ({ 
  title = "Frequently asked questions", 
  subtitle = "Straight answers to the common questions",
  items, 
  accentColor = 'indigo',
  maxItems 
}: FAQProps) {
  const [showAll, setShowAll] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");

  // One brand system. `teal` maps to the agentic accent; every other legacy hue → brand.
  const brand = {
    badge: 'bg-indigo-tint-20 text-royal-indigo border-indigo-tint-20',
    button: 'bg-royal-indigo hover:bg-dark-indigo',
    accent: 'text-royal-indigo',
    border: 'border-indigo-tint-20',
  };
  const accent = {
    badge: 'bg-indigo-tint-20 text-royal-indigo border-indigo-tint-20',
    button: 'bg-royal-indigo hover:bg-dark-indigo',
    accent: 'text-royal-indigo',
    border: 'border-indigo-tint-20',
  };
  const colors = accentColor === 'teal' ? accent : brand;

  // Filter items based on search term
  const filteredItems = items.filter(item =>
    item.question.toLowerCase().includes(searchTerm.toLowerCase()) ||
    item.answer.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Determine which items to show
  const displayItems = maxItems && !showAll 
    ? filteredItems.slice(0, maxItems)
    : filteredItems;

  const hasMoreItems = maxItems && filteredItems.length > maxItems;

  return (
    <section className="py-16 lg:py-24 band-surface">
      <div className="container mx-auto px-4">
        <div className="max-w-4xl mx-auto">
          {/* Header */}
          <div className="text-center mb-12">
            <div className="flex items-center justify-center gap-2 mb-4">
              <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${colors.badge}`}>
                <HelpCircle className={`w-5 h-5 ${colors.accent}`} />
              </div>
              <Badge variant="outline" className={colors.badge}>
                FAQ
              </Badge>
            </div>
            <h2 className="text-[36px] lg:text-[42px] leading-tight font-bold tracking-tight text-dark-indigo mb-4">
              {title}
            </h2>
            <p className="text-[22px] text-soft-black max-w-2xl mx-auto">
              {subtitle}
            </p>
          </div>

          {/* Search */}
          {items.length > 5 && (
            <div className="mb-8">
              <div className="relative max-w-md mx-auto">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <input
                  type="text"
                  placeholder="Search questions"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-3 border border-softblack-tint-20 rounded-lg focus:ring-2 focus:ring-ring focus:border-ring bg-white"
                />
              </div>
            </div>
          )}

          {/* FAQ Items */}
          <Card className="p-0 overflow-hidden">
            <Accordion type="single" collapsible className="w-full">
              {displayItems.map((item, index) => (
                <AccordionItem 
                  key={index} 
                  value={`item-${index}`}
                  className="border-b border-softblack-tint-20 last:border-b-0"
                >
                  <AccordionTrigger className="px-6 py-4 text-left hover:no-underline group">
                    <div className="flex items-start gap-3 pr-4">
                      <div className={`w-6 h-6 rounded-full ${colors.badge} flex items-center justify-center flex-shrink-0 mt-0.5`}>
                        <span className="text-xs font-medium">{index + 1}</span>
                      </div>
                      <span className="group-hover:text-foreground transition-colors">
                        {item.question}
                      </span>
                    </div>
                  </AccordionTrigger>
                  <AccordionContent className="px-6 pb-4">
                    <div className="ml-9 text-muted-foreground leading-relaxed">
                      {item.answer}
                    </div>
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </Card>

          {/* Show More/Less Button */}
          {hasMoreItems && (
            <div className="text-center mt-8">
              <Button
                variant="outline"
                onClick={() => setShowAll(!showAll)}
                className={`${colors.button} text-white border-0 hover:text-white`}
              >
                {showAll ? (
                  <>
                    Show fewer
                    <ChevronDown className="w-4 h-4 ml-2 rotate-180" />
                  </>
                ) : (
                  <>
                    Show all {filteredItems.length} questions
                    <ChevronDown className="w-4 h-4 ml-2" />
                  </>
                )}
              </Button>
            </div>
          )}

          {/* No Results */}
          {searchTerm && filteredItems.length === 0 && (
            <div className="text-center py-8">
              <p className="text-muted-foreground">
                No questions found matching "{searchTerm}". Try a different search term.
              </p>
            </div>
          )}

          {/* Contact CTA */}
          {/* <div className="mt-12 text-center">
            <p className="text-muted-foreground mb-4">
              Still have questions? We're here to help.
            </p>
            <Button 
              variant="outline"
              onClick={() => window.location.href = `mailto:${siteConfig.contact.general}`}
            >
              Contact Support
            </Button>
          </div> */}
        </div>
      </div>
    </section>
  );
}